import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { EventEmitter, inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import {
  defer,
  forkJoin,
  from,
  iif,
  lastValueFrom,
  Observable,
  of
} from "rxjs";
import { UserNotify, UserObserve } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  Invoice,
  InvoiceCreate,
  InvoiceExportType,
  InvoiceFiscalProfile,
  InvoiceFiscalProfileTaxRegime,
  invoiceGetExpiredAt,
  invoiceIsExpired,
  InvoiceItemCreate,
  InvoiceLight,
  InvoicePaymentReceiptPrintViewModel,
  InvoicePaymentRecepit,
  InvoicePaymentRecepitCreate,
  InvoicePaymentTerm,
  InvoicePrintViewModel,
  InvoicePrintViewModelRow,
  InvoiceSending,
  InvoiceSendingStatus,
  InvoiceType,
  PartialInvoice
} from "../models/invoice/invoice";
import {
  capitalize,
  find,
  first,
  flatten,
  groupBy,
  isObject,
  isUndefined,
  last,
  omitBy,
  pickBy,
  round,
  some,
  sum,
  toPairs,
  values
} from "lodash-es";
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  tap,
  toArray
} from "rxjs/operators";
import { TemplateService } from "src/app/services/template.service";
import { dateUtil } from "src/app/support/date-util";
import { sntTranslate } from "src/app/support/translate";
import { formatAddress } from "src/app/common/format/address-format";
import { PrintService } from "src/app/services/print/print.service";
import { getCustomerNominative } from "src/app/support/customer";
import {
  FileNamingService,
  FileType
} from "src/app/services/file-naming.service";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { InvoiceItem } from "src/app/backend/models/invoice/invoice.item";
import * as ibantools from "ibantools";
import { sntConfirm } from "src/app/common/dialog";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { blobToDataUrl } from "src/app/support/blob";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { FiscalProfile } from "src/app/backend/models/fiscal-profile/fiscal-profile";
import { FiscalProfileBackend } from "src/app/backend/api/fiscal-profile.backend";
import { VatNatureType } from "src/app/backend/models/vat-nature-type";
import { SendingType } from "src/app/backend/models/tessera-sanitaria/sending";
import { LetterheadBackend } from "src/app/backend/api/letterhead/letterhead.backend";
import { formatCurrency } from "src/app/support/currency";
import LoadInvoiceSectionalResponseDto from "src/app/backend/models/sectional/sectional";
import { NumberingGapLight } from "src/app/backend/models/invoice/numbering-gaps";
import {
  Regions,
  RegionService
} from "src/app/services/feature-access-control/region.service";
import { ModuleService } from "src/app/services/feature-access-control/module.service";
import { MODULES } from "src/app/core/modules";
import {
  omitUndefined,
  omitUndefinedAndNullProperties
} from "src/app/support/object";
import { ConfigurationBackend } from "src/app/backend/api/configuration.backend";
import { PDFDocument, PDFPage } from "@cantoo/pdf-lib";
import { sift } from "radash";
import { LetterheadLight } from "src/app/backend/models/letterhead/letterhead";
import { chunk } from "es-toolkit";
import pLimit from "p-limit";

@Injectable()
export class InvoiceBackend {
  private readonly http = inject(HttpClient);
  private readonly templateService = inject(TemplateService);
  private readonly printService = inject(PrintService);
  private readonly storageBackend = inject(StorageBackend);
  private readonly fiscalProfileBackend = inject(FiscalProfileBackend);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly patientBackend = inject(PetBackend);
  private readonly letterheadBackend = inject(LetterheadBackend);
  private readonly regionService = inject(RegionService);
  private readonly moduleService = inject(ModuleService);
  private readonly configurationBackend = inject(ConfigurationBackend);
  private readonly fileNamingService = inject(FileNamingService);

  extend(invoice: Invoice): Invoice {
    return extendDoc(invoice);
  }

  load(id: string): Observable<Invoice> {
    return this.http
      .get<Invoice>(`${environment.snoots.api.billing}/invoices/${id}`)
      .pipe(
        switchMap((invoice) =>
          forkJoin({
            invoice: of(invoice),
            redeemedInInvoices: iif(
              () => !!invoice.redeemedIn && invoice.redeemedIn!.length > 0,
              defer(() =>
                forkJoin(invoice.redeemedIn!.map((x) => this.load(x.id)))
              ),
              of([])
            ),
            items: iif(
              () => !!invoice.items && invoice.items.length > 0,
              defer(() =>
                forkJoin(
                  invoice.items.map((x) =>
                    iif(
                      () => !!x.patientId,
                      defer(() =>
                        this.patientBackend.loadLight(x.patientId!).pipe(
                          map((patient) => ({
                            invoiceItem: x,
                            patient
                          })),
                          catchError(() =>
                            of({
                              invoiceItem: x,
                              patient: {
                                name: "",
                                microchip: ""
                              }
                            })
                          )
                        )
                      ),
                      of({
                        invoiceItem: x,
                        patient: {
                          name: "",
                          microchip: ""
                        }
                      })
                    )
                  )
                ).pipe(
                  map((invoiceItemsWithPatients) => {
                    return invoiceItemsWithPatients.map(
                      (x) =>
                        <InvoiceItem>{
                          ...x.invoiceItem,
                          patientName: x.patient.name,
                          patientMicrochip: x.patient.microchip
                        }
                    );
                  })
                )
              ),
              of([])
            )
          })
        ),
        map(({ invoice, redeemedInInvoices, items }) => {
          return <Invoice>{
            ...invoice,
            redeemedInInvoices,
            items
          };
        }),
        map((invoice) => extendDoc(invoice))
      );
  }

  loadLight(id: string): Observable<InvoiceLight> {
    return this.http
      .get<InvoiceLight>(`${environment.snoots.api.billing}/invoices/${id}`, {
        params: {
          light: true
        }
      })
      .pipe(
        map((lightInvoice) => {
          return {
            ...lightInvoice,
            isDraft: !lightInvoice.emittedBy
          };
        })
      );
  }

  loadCreditNoteByReversedInvoice(
    reversedInvoiceId: string
  ): Observable<InvoiceLight | undefined> {
    return this.http
      .get<PaginatedResult<InvoiceLight>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: {
            p: "1",
            s: "100000",
            type: "credit-note",
            reversedInvoiceId: reversedInvoiceId,
            light: true,
            emitted: true
          }
        }
      )
      .pipe(
        map((result) => {
          if (result?.docs && result?.docs?.length > 0) {
            return first(result.docs)!;
          }

          return undefined;
        })
      );
  }

  listDraftByCustomer({
    customerId
  }: {
    customerId: string;
  }): Observable<InvoiceLight[]> {
    return this.listByCustomerLight(customerId, false, {
      page: 1,
      pageSize: 1000
    }).pipe(
      map((response) => response.docs.filter((invoice) => invoice.isDraft))
    );
  }

  #listAllLight({
    paid,
    customerId,
    emitted
  }: {
    paid?: boolean;
    customerId?: string;
    emitted?: boolean;
  }): Observable<InvoiceLight[]> {
    return this.http
      .get<PaginatedResult<InvoiceLight>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: omitUndefinedAndNullProperties({
            paid,
            customerId,
            emitted,
            light: true
          })
        }
      )
      .pipe(
        map((response: PaginatedResult<InvoiceLight>) => {
          return response.docs.map((invoice: InvoiceLight) => {
            return {
              ...extendDocLight(invoice),
              isDraft: !invoice.emittedBy
            };
          });
        })
      );
  }

  listAllByCustomerLight({
    customerId,
    emitted
  }: {
    customerId: string;
    emitted: boolean;
  }): Observable<InvoiceLight[]> {
    return this.#listAllLight({ customerId, emitted });
  }

  listByCustomerLight(
    customerId: string,
    emitted: boolean,
    request: PageRequest
  ): Observable<PaginatedResult<InvoiceLight>> {
    return this.http
      .get<PaginatedResult<InvoiceLight>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: omitUndefinedAndNullProperties({
            customerId: customerId,
            emitted: emitted,
            p: request.page,
            s: request.pageSize,
            light: true
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map((invoiceLight) => {
              return {
                ...invoiceLight,
                isDraft: !invoiceLight.emittedBy
              };
            })
          };
        })
      );
  }

  listAllForPaymentLight(
    type: string,
    customerId: string,
    emitted: boolean,
    redeemed: boolean,
    fiscalProfileId: string
  ): Observable<InvoiceLight[]> {
    return this.http
      .get<PaginatedResult<InvoiceLight>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: {
            type: type,
            customerId: customerId,
            emitted: emitted,
            redeemed: redeemed,
            fiscalProfileId: fiscalProfileId,
            p: 1,
            s: 10000,
            light: true
          }
        }
      )
      .pipe(
        map((result) => {
          return result.docs;
        })
      );
  }

  listAllByTypeAndCustomer(
    type: string | undefined,
    customerId: string,
    emitted: boolean
  ): Observable<Invoice[]> {
    return this.http
      .get<PaginatedResult<Invoice>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: <any>pickBy({
            type: type,
            emitted: emitted,
            customerId: customerId
          })
        }
      )
      .pipe(
        map((result) => {
          return result.docs;
        })
      );
  }

  listForPayment(
    type: string,
    customerId: string,
    emitted: boolean,
    redeemed: boolean,
    fiscalProfileId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Invoice>> {
    return this.http
      .get<PaginatedResult<Invoice>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: omitUndefinedAndNullProperties({
            type: type,
            customerId: customerId,
            emitted: emitted,
            redeemed: redeemed,
            fiscalProfileId: fiscalProfileId,
            p: request.page,
            s: request.pageSize
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  listByTypeAndCustomer(
    type: string,
    customerId: string,
    request: Partial<PageRequest> & {
      createdAtFrom?: string | undefined;
      createdAtTo?: string | undefined;
      emittedAtFrom?: string | undefined;
      emittedAtTo?: string | undefined;
      number?: string | undefined;
      paid?: "true" | "false" | undefined;
      sendingType?: SendingType | undefined;
      sendingStatus?: InvoiceSendingStatus[] | undefined;
      select?: string | undefined;
      emitted?: boolean;
      notSent?: boolean;
    }
  ): Observable<PaginatedResult<Invoice>> {
    return this.http
      .get<PaginatedResult<Invoice>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: <any>pickBy({
            type: type,
            customerId: customerId,
            p: request.page,
            s: request.pageSize,
            createdAtFrom: request.createdAtFrom,
            createdAtTo: request.createdAtTo,
            emittedAtFrom: request.emittedAtFrom || undefined,
            emittedAtTo: request.emittedAtTo || undefined,
            number: request.number || undefined,
            paid: request.paid || undefined,
            select: request.select || undefined,
            emitted: request.emitted ? "true" : "false",
            sendingType: request.sendingType || undefined,
            sendingStatus:
              request.sendingStatus && request.sendingStatus.length > 0
                ? request.sendingStatus.join(",")
                : undefined,
            notSent: request.notSent || undefined
          })
        }
      )
      .pipe(
        map((result) => {
          return <PaginatedResult<Invoice>>{
            ...result,
            docs: !request.select ? result.docs.map(extendDoc) : result.docs
          };
        })
      );
  }

  listByTypeAndCustomerLight(
    type: string | undefined,
    customerId: string,
    request: Partial<PageRequest> & {
      createdAtFrom?: string | undefined;
      createdAtTo?: string | undefined;
      emittedAtFrom?: string | undefined;
      emittedAtTo?: string | undefined;
      number?: string | undefined;
      paid?: "true" | "false" | undefined;
      sendingType?: SendingType | undefined;
      sendingStatus?: InvoiceSendingStatus[] | undefined;
      select?: string | undefined;
      emitted?: boolean;
      notSent?: boolean;
      fiscalProfileId?: string | undefined;
      paymentMethodId?: string | undefined;
    }
  ): Observable<PaginatedResult<InvoiceLight>> {
    return this.http
      .get<PaginatedResult<InvoiceLight>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: <any>pickBy({
            light: true,
            type: type,
            customerId: customerId,
            p: request.page,
            s: request.pageSize,
            createdAtFrom: request.createdAtFrom,
            createdAtTo: request.createdAtTo,
            emittedAtFrom: request.emittedAtFrom || undefined,
            emittedAtTo: request.emittedAtTo || undefined,
            number: request.number || undefined,
            paid: request.paid || undefined,
            select: request.select || undefined,
            emitted: request.emitted ? "true" : "false",
            sendingType: request.sendingType || undefined,
            sendingStatus:
              request.sendingStatus && request.sendingStatus.length > 0
                ? request.sendingStatus.join(",")
                : undefined,
            notSent: request.notSent || undefined,
            fiscalProfileId: request.fiscalProfileId || undefined,
            paymentMethodId: request.paymentMethodId || undefined
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDocLight)
          };
        })
      );
  }

  #listByType(
    type: string,
    request: Partial<PageRequest> & {
      createdAtFrom?: string | undefined;
      createdAtTo?: string | undefined;
      emittedAtFrom?: string | undefined;
      emittedAtTo?: string | undefined;
      number?: string | undefined;
      paid?: "true" | "false" | undefined;
      sendingType?: SendingType | undefined;
      sendingStatus?: InvoiceSendingStatus[] | undefined;
      select?: string | undefined;
      emitted?: boolean;
      notSent?: boolean;
      fiscalProfileId?: string | undefined;
      customerId?: string | undefined;
      paymentMethodId?: string | undefined;
    }
  ): Observable<PaginatedResult<Invoice>> {
    return this.http
      .get<PaginatedResult<Invoice>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: <any>pickBy({
            type: type,
            p: request.page,
            s: request.pageSize,
            createdAtFrom: request.createdAtFrom,
            createdAtTo: request.createdAtTo,
            emittedAtFrom: request.emittedAtFrom || undefined,
            emittedAtTo: request.emittedAtTo || undefined,
            number: request.number || undefined,
            paid: request.paid || undefined,
            customerId: request.customerId || undefined,
            select: request.select || undefined,
            emitted: request.emitted ? "true" : "false",
            sendingType: request.sendingType || undefined,
            sendingStatus:
              request.sendingStatus && request.sendingStatus.length > 0
                ? request.sendingStatus.join(",")
                : undefined,
            notSent: request.notSent || undefined,
            fiscalProfileId: request.fiscalProfileId || undefined,
            paymentMethodId: request.paymentMethodId || undefined,
            ...request.filterValues
          })
        }
      )
      .pipe(
        map((result) => {
          return <PaginatedResult<Invoice>>{
            ...result,
            docs: !request.select ? result.docs.map(extendDoc) : result.docs
          };
        })
      );
  }

  listDraftByType(
    type: string,
    request: Partial<PageRequest> & {
      createdAtFrom?: string | undefined;
      createdAtTo?: string | undefined;
      emittedAtFrom?: string | undefined;
      emittedAtTo?: string | undefined;
      number?: string | undefined;
      select?: string | undefined;
      fiscalProfileId?: string | undefined;
      customerId?: string | undefined;
      paymentMethodId?: string | undefined;
    }
  ): Observable<PaginatedResult<Invoice>> {
    return this.#listByType(type, {
      ...request,
      emitted: false
    });
  }

  listDraftByTypeLight(
    type: string,
    request: Partial<PageRequest> & {
      createdAtFrom?: string | undefined;
      createdAtTo?: string | undefined;
      emittedAtFrom?: string | undefined;
      emittedAtTo?: string | undefined;
      number?: string | undefined;
      select?: string | undefined;
      fiscalProfileId?: string | undefined;
      customerId?: string | undefined;
      paymentMethodId?: string | undefined;
    }
  ): Observable<PaginatedResult<Invoice>> {
    return this.#listByType(type, {
      ...request,
      emitted: false,
      filterValues: {
        light: true
      }
    });
  }

  listEmittedByType(
    type: string,
    request: Partial<PageRequest> & {
      emittedAtFrom: string | undefined;
      emittedAtTo: string | undefined;
      number: string | undefined;
      paid: "true" | "false" | undefined;
      select?: string | undefined;
      sendingType?: SendingType | undefined;
      sendingStatus?: InvoiceSendingStatus[] | undefined;
      notSent?: boolean;
      fiscalProfileId?: string | undefined;
      customerId?: string | undefined;
      paymentMethodId?: string | undefined;
    }
  ): Observable<PaginatedResult<Invoice>> {
    return this.#listByType(type, {
      ...request,
      emitted: true
    });
  }

  listBySendingId(
    sendingId: string,
    message: boolean,
    request: PageRequest
  ): Observable<PaginatedResult<Invoice>> {
    return this.http
      .get<PaginatedResult<Invoice>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: omitUndefinedAndNullProperties({
            p: request.page,
            s: request.pageSize,
            emitted: true,
            sendingMessages: message,
            sendingId: sendingId
          })
        }
      )
      .pipe(
        map((result) => {
          if (!message) {
            return result;
          }

          return {
            ...result,
            docs: result.docs.filter((invoice) => {
              const sending = invoice.sendings?.find((x) => x.id === sendingId);
              if (!sending) {
                return false;
              }

              return !!sending.messages && sending.messages.length > 0;
            })
          };
        }),
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  listToSendTS(
    sendingId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Invoice>> {
    return this.http
      .get<PaginatedResult<Invoice>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: omitUndefined({
            p: request.page,
            s: request.pageSize,
            sendingId: sendingId,
            emitted: true
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  listBySending(
    type: string,
    message: boolean,
    request: PageRequest
  ): Observable<PaginatedResult<Invoice>> {
    return this.http
      .get<PaginatedResult<Invoice>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: omitUndefined({
            type: type,
            p: request.page,
            s: request.pageSize,
            sendingMessages: message,
            emitted: true
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  listAllByTicketLight(ticketId: string): Observable<InvoiceLight[]> {
    return this.http
      .get<PaginatedResult<InvoiceLight>>(
        `${environment.snoots.api.billing}/invoices`,
        {
          params: {
            p: "1",
            s: "10000",
            light: true,
            ticket: ticketId,
            emitted: true
          }
        }
      )
      .pipe(
        map((result) => {
          return result.docs;
        })
      );
  }

  listByQuote(quoteId: string, emitted: boolean): Observable<InvoiceLight[]> {
    return this.http
      .get<PaginatedResult<InvoiceLight>>(
        `${environment.snoots.api.billing}/quotes/${quoteId}/invoices`,
        {
          params: {
            light: true,
            emitted: emitted
          }
        }
      )
      .pipe(map((response) => response.docs));
  }

  listEmittedByQuoteAndType({
    quoteId,
    type
  }: {
    quoteId: string;
    type: InvoiceType;
  }): Observable<InvoiceLight[]> {
    return this.http
      .get<PaginatedResult<InvoiceLight>>(
        `${environment.snoots.api.billing}/quotes/${quoteId}/invoices`,
        {
          params: {
            type,
            emitted: true,
            light: true
          }
        }
      )
      .pipe(map((response) => response.docs));
  }

  listAllUnpaidLight({
    customerId
  }: {
    customerId?: string;
  }): Observable<InvoiceLight[]> {
    return this.#listAllLight({
      paid: false,
      emitted: true,
      customerId
    });
  }

  listExpiredByCustomerLight({
    customerId
  }: {
    customerId: string;
  }): Observable<InvoiceLight[]> {
    return this.listAllByCustomerLight({ customerId, emitted: true }).pipe(
      map((invoices: InvoiceLight[]) => {
        return invoices.filter((invoice: InvoiceLight) => invoice.expired);
      })
    );
  }

  @UserNotify({
    successMessage: marker("Fattura creata"),
    errorMessage: marker("Impossibile creare la fattura"),
    progressIndeterminate: true,
    progressMessage: marker("Creazione fattura")
  })
  create(payload: Partial<Invoice>): Observable<Invoice> {
    return this.createSilently(payload);
  }

  @UserNotify({
    successMessage: marker("Fattura creata"),
    errorMessage: marker("Impossibile creare la fattura"),
    progressIndeterminate: true,
    progressMessage: marker("Creazione fattura")
  })
  createByCustomer(
    customerId: string,
    payload: Partial<Invoice> & {
      patients: string[];
    }
  ): Observable<Invoice> {
    return this.http.post<Invoice>(
      `${environment.snoots.api.billing}/customers/${customerId}/invoices`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Fattura creata"),
    errorMessage: marker("Impossibile creare la fattura"),
    progressIndeterminate: true,
    progressMessage: marker("Creazione fattura")
  })
  createWithItems(
    payload: Partial<Invoice>,
    items: InvoiceItemCreate[]
  ): Observable<Invoice> {
    const effecitvePayload = <any>{
      ...payload
    };

    if (!!items && items.length > 0) {
      effecitvePayload.items = items;
    }

    return this.createSilently(effecitvePayload);
  }

  createSilently(payload: Partial<Invoice>): Observable<Invoice> {
    return this.http.post<Invoice>(
      `${environment.snoots.api.billing}/invoices`,
      payload
    );
  }

  createByCart(cartId: string, payload: InvoiceCreate): Observable<Invoice> {
    return this.http.post<Invoice>(
      `${environment.snoots.api.billing}/carts/${cartId}/invoices`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Fattura creata"),
    errorMessage: marker("Impossibile creare la Fattura"),
    progressIndeterminate: true,
    progressMessage: marker("Creazione fattura")
  })
  createByBook(
    bookId: string,
    payload: Partial<Invoice> & {
      bookItemIds: string[];
    }
  ): Observable<Invoice> {
    return this.http.post<Invoice>(
      `${environment.snoots.api.billing}/books/${bookId}/invoices`,
      payload
    );
  }

  emit(
    id: string,
    emit?: {
      numberingGapId: string;
      number: string;
      emittedAt: Date;
    }
  ): Observable<Invoice> {
    return this.http
      .put<void>(`${environment.snoots.api.billing}/invoices/${id}/emit`, emit)
      .pipe(switchMap(() => this.load(id)));
  }

  emitAndCashOut(
    invoice: Invoice,
    emit?: {
      numberingGapId: string;
      number: string;
      emittedAt: Date;
    }
  ): Observable<Invoice> {
    return this.http
      .put<void>(
        `${environment.snoots.api.billing}/invoices/${invoice.id}/emit`,
        emit
      )
      .pipe(
        switchMap(() =>
          iif(
            () =>
              invoice.type === "advance-invoice" || (invoice.toBePaid || 0) > 0,
            defer(() =>
              this.http.post<void>(
                `${environment.snoots.api.billing}/invoices/${invoice.id}/payments`,
                {
                  amount: invoice.toBePaid,
                  paidAt: dateUtil().toISOString(),
                  method: invoice.method.id
                }
              )
            ),
            of(void 0)
          ).pipe(switchMap(() => this.load(invoice.id)))
        )
      );
  }

  @UserNotify({
    successMessage: marker("Fatture esportate in XML"),
    errorMessage: marker("Impossibile esportare le fatture")
  })
  exportMassiveXML(
    exportOptions: {
      filter?: Record<string, any>;
      includeIds?: string[];
      excludeIds?: string[];
    },
    type: string
  ): Observable<any> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/electronic-invoice/invoices/exports`,
      {
        ...exportOptions,
        type: type
      }
    );
  }

  @UserObserve({
    loading: (invoice: Invoice) =>
      sntTranslate(marker("Esporto in XML la invoiceType invoiceNumber"), {
        invoiceType: sntTranslate(invoice.type),
        invoiceNumber: invoice.number
      }),
    success: (invoice: Invoice) =>
      sntTranslate(marker("invoiceType invoiceNumber esportata in XML"), {
        invoiceType: capitalize(sntTranslate(invoice.type)),
        invoiceNumber: invoice.number
      }),
    error: (invoice: Invoice) =>
      sntTranslate(
        marker("Impossibile esportare in XML la invoiceType invoiceNumber"),
        {
          invoiceType: sntTranslate(invoice.type),
          invoiceNumber: invoice.number
        }
      )
  })
  exportXML(
    invoice: Invoice
  ): Observable<{ fileName: string; content: string }> {
    return this.http.put<{ fileName: string; content: string }>(
      `${environment.snoots.api.billing}/electronic-invoice/invoices/${invoice.id}/export`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Fatture esportate in Excel"),
    errorMessage: marker("Impossibile esportare le fatture")
  })
  exportMassiveExcel(
    exportOptions: {
      filter?: Record<string, any>;
      includeIds?: string[];
      excludeIds?: string[];
    },
    type: string
  ): Observable<any> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/invoices/exports`,
      {
        ...exportOptions,
        type: type
      }
    );
  }

  @UserNotify({
    successMessage: marker("Righe di fattura esportate in Excel"),
    errorMessage: marker("Impossibile esportare le righe di fattura")
  })
  exportMassiveItemsCSV(exportOptions: {
    filter?: Record<string, any>;
    includeIds?: string[];
    excludeIds?: string[];
  }): Observable<any> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/invoices/exports`,
      {
        ...exportOptions,
        type: "invoice-item"
      }
    );
  }

  @UserNotify({
    successMessage: marker("Righe di fattura esportate in Excel"),
    errorMessage: marker("Impossibile esportare le righe di fattura")
  })
  exportMassiveItemsPerPaymentMethod(exportOptions: {
    filter?: Record<string, any>;
    includeIds?: string[];
    excludeIds?: string[];
  }): Observable<any> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/invoices/exports`,
      {
        ...exportOptions,
        type: "invoice-item-per-payment-method"
      }
    );
  }

  @UserNotify({
    successMessage: marker("Righe di fattura esportate in Excel"),
    errorMessage: marker("Impossibile esportare le righe di fattura")
  })
  exportMassiveItemsPerVat(exportOptions: {
    filter?: Record<string, any>;
    includeIds?: string[];
    excludeIds?: string[];
  }): Observable<any> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/invoices/exports`,
      {
        ...exportOptions,
        type: "invoice-item-per-vat"
      }
    );
  }

  @UserNotify({
    successMessage: (ids: string[], type: InvoiceExportType) => {
      switch (type) {
        case InvoiceExportType.TEAM_SYSTEM: {
          return marker("Tracciato esportato per Team System");
        }
        case InvoiceExportType.ZUCCHETTI_AD_HOC: {
          return marker("Tracciato esportato per Zucchetti Ad Hoc");
        }
        case InvoiceExportType.INVOICE:
        case InvoiceExportType.INVOICE_DRAFT:
        case InvoiceExportType.PAYMENT:
        case InvoiceExportType.INVOICE_ITEM:
        default: {
          return marker("Tracciato esportato per gestionale esterno");
        }
      }
    },
    errorMessage: marker("Impossibile esportare le righe di fattura")
  })
  exportMassiveItemsTeamSystem(
    exportOptions: {
      filter?: Record<string, any>;
      includeIds?: string[];
      excludeIds?: string[];
    },
    type: InvoiceExportType
  ): Observable<any> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/invoices/exports`,
      {
        ...exportOptions,
        type: type
      }
    );
  }

  @UserNotify({
    successMessage: marker("Fattura aggiornata"),
    errorMessage: marker("Impossibile aggiornare la fattura")
  })
  update(id: string, request: Partial<Invoice>): Observable<void> {
    return this.updateSilently(id, request);
  }

  private updateSilently(
    id: string,
    request: PartialInvoice
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/invoices/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Modalità di pagamento aggiornata"),
    errorMessage: marker("Impossibile aggiornare la modalità di pagamento")
  })
  updatePaymentMethod(id: string, method: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/invoices/${id}`,
      <Partial<Invoice>>{
        method
      }
    );
  }

  @UserNotify({
    successMessage: marker("Tipo fattura cambiato"),
    errorMessage: marker("Impossibile cambiare il tipo della fattura")
  })
  updateType(invoiceId: string, type: InvoiceType): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/invoices/${invoiceId}`,
      <Partial<Invoice>>{
        type
      }
    );
  }

  @UserNotify({
    successMessage: marker("Modalità di pagamento aggiornata"),
    errorMessage: marker("Impossibile aggiornare la modalità di pagamento")
  })
  updatePaymentTerm(
    id: string,
    paymentTerm: InvoicePaymentTerm
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/invoices/${id}`,
      <Partial<Invoice>>{
        paymentTerm
      }
    );
  }

  @UserNotify({
    successMessage: marker("Imposta di bollo inserita"),
    errorMessage: marker("Impossibile inserire l'imposta di bollo")
  })
  enableStampTax(id: string): Observable<void> {
    return this.updateSilently(id, {
      stampTax: true
    });
  }

  @UserNotify({
    successMessage: marker("Tessera sanitaria impostata"),
    errorMessage: marker("Impossibile impostare tessera sanitaria")
  })
  enableHealthCard(id: string): Observable<void> {
    return this.updateSilently(id, {
      tesseraSanitaria: true
    });
  }

  @UserNotify({
    successMessage: marker("Arrotondamento commerciale impostato in fattura"),
    errorMessage: marker(
      "Impossibile impostare l'arrotondamento commerciale in fattura"
    )
  })
  enableCommercialRounding(
    id: string,
    fiscalProfile: InvoiceFiscalProfile
  ): Observable<void> {
    return this.updateSilently(id, {
      fiscalProfile: {
        ...fiscalProfile,
        enableCommercialRounding: true
      }
    });
  }

  @UserNotify({
    successMessage: marker("Fattura impostata come elettronica"),
    errorMessage: marker("Impossibile impostare la fattura come elettronica")
  })
  enableFE(id: string): Observable<void> {
    return this.updateSilently(id, {
      electronicInvoice: true
    });
  }

  @UserNotify({
    successMessage: marker("Imposta di bollo rimossa"),
    errorMessage: marker("Impossibile rimuovere l'imposta di bollo")
  })
  disableStampTax(id: string): Observable<void> {
    return this.updateSilently(id, {
      stampTax: false
    });
  }

  @UserNotify({
    successMessage: marker("Tessera sanitaria"),
    errorMessage: marker("Impossibile rimuovere tessera sanitaria")
  })
  disableHealthCard(id: string): Observable<void> {
    return this.updateSilently(id, {
      tesseraSanitaria: false
    });
  }

  @UserNotify({
    successMessage: marker(
      "Arrotondamento commerciale disabilitato in fattura"
    ),
    errorMessage: marker(
      "Impossibile disabilitare l'arrotondamento commerciale in fattura"
    )
  })
  disableCommercialRounding(
    id: string,
    fiscalProfile: InvoiceFiscalProfile
  ): Observable<void> {
    return this.updateSilently(id, {
      fiscalProfile: {
        ...fiscalProfile,
        enableCommercialRounding: false
      }
    });
  }

  @UserNotify({
    successMessage: marker("Fattura impostata come non elettronica"),
    errorMessage: marker(
      "Impossibile impostare la fattura come non elettronica"
    )
  })
  disableFE(id: string): Observable<void> {
    return this.updateSilently(id, {
      electronicInvoice: false
    });
  }

  @UserNotify({
    successMessage: marker("Fattura impostata come Extra UE"),
    errorMessage: marker("Impossibile impostare la fattura come Extra UE")
  })
  setCustomerExtraUE(id: string): Observable<void> {
    return this.load(id).pipe(
      switchMap((invoice) => {
        return this.updateSilently(id, {
          customer: {
            ...invoice.customer,
            extraUe: true
          }
        });
      })
    );
  }

  @UserNotify({
    successMessage: marker("Fattura impostata come UE"),
    errorMessage: marker("Impossibile impostare la fattura come UE")
  })
  unsetCustomerExtraUE(id: string): Observable<void> {
    return this.load(id).pipe(
      switchMap((invoice) => {
        return this.updateSilently(id, {
          customer: {
            ...invoice.customer,
            extraUe: false
          }
        });
      })
    );
  }

  @UserNotify({
    successMessage: marker("Fattura eliminato"),
    errorMessage: (error: HttpErrorResponse) => {
      switch (error.status) {
        case 409: {
          return sntTranslate(
            marker("La fattura è bloccata, non può essere eliminata.")
          );
        }
        default: {
          return marker("Impossibile eliminare la fattura");
        }
      }
    }
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.billing}/invoices/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Fatture eliminate"),
    errorMessage: marker("Impossibile eliminare le fatture")
  })
  deleteBulk(ids: string[]): Observable<unknown> {
    if (ids.length === 0) {
      return of(void 0);
    }

    return this.http.delete<void>(
      `${environment.snoots.api.billing}/invoices`,
      {
        body: {
          invoiceIds: ids
        }
      }
    );
  }

  @UserNotify({
    successMessage: marker("Piano rateale creato"),
    errorMessage: marker("Impossibile creare il piano rateale")
  })
  createInstallmentes(
    id: string,
    installmentsPlan: number[],
    endOfMonth: boolean
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.billing}/invoices/${id}/installments`,
      {
        installmentsPlan: installmentsPlan,
        endOfMonth: endOfMonth
      }
    );
  }

  @UserNotify({
    successMessage: marker("Incasso inserito"),
    errorMessage: (error: any) => {
      if (error.status === 409) {
        return sntTranslate(marker("Prima di incassare emettere la fattura"));
      }
      return sntTranslate(marker("Impossibile inserire l'incasso"));
    }
  })
  addPayment(
    invoiceId: string,
    payment: InvoicePaymentRecepitCreate
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.billing}/invoices/${invoiceId}/payments`,
      payment
    );
  }

  @UserNotify({
    successMessage: marker("Incasso aggiornato"),
    errorMessage: marker("Impossibile aggiornare l'incasso")
  })
  updatePayment(
    invoiceId: string,
    itemId: string,
    payment: Partial<InvoicePaymentRecepit>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/invoices/${invoiceId}/payments/${itemId}`,
      payment
    );
  }

  @UserNotify({
    successMessage: marker("Incasso rimosso"),
    errorMessage: marker("Impossibile rimuovere l'incasso")
  })
  deletePayment(invoiceId: string, itemId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.billing}/invoices/${invoiceId}/payments/${itemId}`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Nota di credito emessa"),
    errorMessage: marker("Impossibile emettere la nota di credito")
  })
  createCreditNote(
    id: string,
    pricesAndQuantityConfig: Array<{
      itemId: string;
      name: string;
      quantity: number;
      unitPrice: number;
      discount: number | undefined;
    }>
  ): Observable<Invoice> {
    return this.http.post<Invoice>(
      `${environment.snoots.api.billing}/invoices/${id}/credit-notes`,
      {
        items: pricesAndQuantityConfig
      }
    );
  }

  @UserNotify({
    successMessage: (id: string, item: InvoiceItem) =>
      `${item.name} ${sntTranslate(marker("aggiunto alla fattura"))}`,
    errorMessage: marker("Impossibile aggiungere alla fattura")
  })
  addItem(
    invoice: Invoice,
    invoiceItemCreate: InvoiceItemCreate
  ): Observable<void> {
    return this.addItemSilently(invoice, invoiceItemCreate);
  }

  addItemSilently(
    invoice: Invoice,
    invoiceItemCreate: InvoiceItemCreate
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.billing}/invoices/${invoice.id}/items`,
      invoiceItemCreate
    );
  }

  @UserNotify({
    successMessage: (id: string, item: Partial<InvoiceItem>) =>
      `${item.name} ${sntTranslate(marker("rimosso"))}`,
    errorMessage: marker("Impossibile rimuovere la riga")
  })
  removeItem(id: string, item: InvoiceItem): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.billing}/invoices/${id}/items/${item.id}`
    );
  }

  @UserNotify({
    successMessage: (
      id: string,
      itemId: string,
      item: Partial<InvoiceItem>,
      itemName: string
    ) => `${itemName} ${sntTranslate(marker("aggiornato"))}`,
    errorMessage: marker("Impossibile aggiornare la riga")
  })
  updateItem(
    id: string,
    itemId: string,
    item: Partial<InvoiceItem>,
    itemName: string
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/invoices/${id}/items/${itemId}`,
      item
    );
  }

  unloadItem({
    invoiceId,
    itemId,
    unloadingId
  }: {
    invoiceId: string;
    itemId: string;
    unloadingId: string;
  }): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/invoices/${invoiceId}/items/${itemId}`,
      <Partial<InvoiceItem>>{
        unloadingId
      }
    );
  }

  getByMessageEvent(invoice: Invoice, message: MessageEvent<Invoice>): Invoice {
    return extendDoc({
      ...invoice,
      ...message.data
    });
  }

  async print(invoice: Invoice, creditNote?: InvoiceLight): Promise<void> {
    try {
      await this.printService.printPdf(
        this.toPdf(invoice, creditNote),
        this.fileNamingService.getFileName({
          fileType: FileType.Invoice,
          context: { invoice }
        })
      );
    } catch (ex) {
      console.error(ex);
      throw ex;
    }
  }

  async printMultiple(
    invoicesId: string[],
    progress?: EventEmitter<number>
  ): Promise<void> {
    const chunks = chunk(invoicesId, 20);

    const pdfPromises: Promise<Blob>[] = [];

    let stepForInvoice = 0.5 / invoicesId.length;
    let stepForPdf = 0.48 / (chunks.length * 2);

    const limit = pLimit(30);

    for (const chunk of chunks) {
      const invoices: Invoice[] = await Promise.all(
        chunk.map((invoiceId: string) =>
          limit(() =>
            lastValueFrom(this.load(invoiceId)).then((invoice: Invoice) => {
              progress?.emit(stepForInvoice);
              return invoice;
            })
          )
        )
      );

      pdfPromises.push(
        limit(() =>
          this.toPdfMultiple(
            invoices.map((invoice: Invoice) => {
              return { invoice };
            })
          ).then((pdf) => {
            progress?.emit(stepForPdf);
            return pdf;
          })
        )
      );
    }

    const blobs = await Promise.all(pdfPromises);

    const mergedPdf = await PDFDocument.create();

    for (const blob of blobs) {
      const arrayBuffer = await blob.arrayBuffer();
      const pdfDocument = await PDFDocument.load(arrayBuffer);

      const copiedPages = await mergedPdf.copyPages(
        pdfDocument,
        pdfDocument.getPageIndices()
      );

      copiedPages.forEach((page) => {
        mergedPdf.addPage(page);
      });

      progress?.emit(stepForPdf);
    }

    const mergedUInt8Array = await mergedPdf.save();
    const mergedBlob = new Blob([<any>mergedUInt8Array], {
      type: "application/pdf"
    });

    progress?.emit(1);

    await this.printService.printPdf(
      Promise.resolve(mergedBlob),
      this.fileNamingService.getFileName({
        fileType: FileType.MergedInvoices
      })
    );
  }

  async printPaymentReceipt({ invoice }: { invoice: Invoice }): Promise<void> {
    try {
      await this.printService.printPdf(
        this.#toPaymentReceiptPdf(invoice),
        this.fileNamingService.getFileName({
          fileType: FileType.PaymentReceipt,
          context: { invoice }
        })
      );
    } catch (ex) {
      console.error(ex);
      throw ex;
    }
  }

  mergeInvoices(
    ids: string[],
    progress: EventEmitter<number>
  ): Observable<Blob> {
    let mergedPdf: PDFDocument;

    return forkJoin({
      pdf: from(PDFDocument.create()),
      ids: of(ids)
    }).pipe(
      switchMap(({ pdf, ids }) => {
        mergedPdf = pdf;
        return ids;
      }),
      mergeMap((id) =>
        this.load(id).pipe(
          switchMap((invoice) => {
            progress.emit(0.5);
            return from(
              this.toPdf(invoice)
                .then((blob) => blob.arrayBuffer())
                .then((buffer) => PDFDocument.load(buffer))
                .then((pdf) => ({ pdf, id }))
            );
          })
        )
      ),
      toArray(),
      map(async (list) => {
        const pagesList: Array<PDFPage[]> = [];

        for (const idInvoice of ids) {
          let pdf = list.find((item) => item.id === idInvoice)!.pdf;

          const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());

          pagesList.push(pages);
        }

        return pagesList;
      }),
      switchMap((pagesListToLoad) =>
        from(
          pagesListToLoad
            .then((pagesList) => {
              pagesList.forEach((pages) => {
                progress.emit(0.5);
                return pages.forEach((page) => mergedPdf.addPage(page));
              });
            })
            .then(() => mergedPdf.save())
            .then(
              (content) => new Blob([<any>content], { type: "application/pdf" })
            )
        )
      )
    );
  }

  @UserNotify({
    successMessage: marker("Fattura inviata"),
    errorMessage: marker("Impossibile inviare la fattura")
  })
  sendAndUpdateEmail(options: {
    invoice: Invoice;
    email: string;
    emailValueChanged: boolean;
    includePaymentReceipt: boolean;
    note?: string;
  }): Observable<unknown> {
    const { invoice, email, emailValueChanged, includePaymentReceipt, note } =
      options;
    return defer(() =>
      forkJoin({
        invoicePdfBase64Blob: from(this.toPdf(invoice)),
        paymentReceiptPdfBase64Blob: iif(
          () => includePaymentReceipt,
          defer(() => from(this.#toPaymentReceiptPdf(invoice))),
          of(null)
        )
      })
    ).pipe(
      switchMap(({ invoicePdfBase64Blob, paymentReceiptPdfBase64Blob }) => {
        return forkJoin({
          invoiceStorageId: this.updateStorage(invoice, invoicePdfBase64Blob),
          paymentReceiptStorageId: iif(
            () => !!paymentReceiptPdfBase64Blob,
            defer(() => {
              return this.storageBackend
                .upload(
                  new File(
                    [paymentReceiptPdfBase64Blob!],
                    this.fileNamingService.getFileName({
                      fileType: FileType.PaymentReceipt,
                      context: { invoice }
                    })
                  ),
                  null,
                  false
                )
                .pipe(map((uploadResult) => uploadResult.id));
            }),
            of(null)
          )
        });
      }),
      switchMap(({ invoiceStorageId, paymentReceiptStorageId }) => {
        return this.http
          .put<void>(
            `${environment.snoots.api.billing}/invoices/${invoice.id}/send`,
            {
              email,
              notes: note,
              attachments: sift([
                {
                  contentType: "application/pdf",
                  fileName: this.fileNamingService.getFileName({
                    fileType: FileType.Invoice,
                    context: { invoice }
                  }),
                  storageId: invoiceStorageId
                },
                paymentReceiptStorageId
                  ? {
                      contentType: "application/pdf",
                      fileName: this.fileNamingService.getFileName({
                        fileType: FileType.PaymentReceipt,
                        context: { invoice }
                      }),
                      storageId: paymentReceiptStorageId
                    }
                  : null
              ])
            }
          )
          .pipe(
            switchMap(() =>
              iif(
                () => emailValueChanged,
                defer(() =>
                  (async () => {
                    const result = await sntConfirm(
                      sntTranslate(marker("Dati cliente")),
                      sntTranslate(
                        marker(
                          "L'indirizzo email di destinazione è diverso da quello del cliente, vuoi aggiornarlo?"
                        )
                      )
                    );

                    if (result) {
                      await lastValueFrom(
                        this.customerBackend.update(
                          {
                            email
                          },
                          invoice.customer!.id
                        )
                      );
                    }
                  })()
                ),
                of(true)
              )
            )
          );
      })
    );
  }

  async updateStorage(invoice: Invoice, blob: Blob): Promise<string> {
    if (!invoice.storageId) {
      const uploadResult = await lastValueFrom(
        this.storageBackend.upload(
          new File(
            [blob],
            this.fileNamingService.getFileName({
              fileType: FileType.Invoice,
              context: { invoice }
            })
          ),
          null,
          false
        )
      );

      await lastValueFrom(
        this.update(invoice.id, {
          storageId: uploadResult.id
        })
      );

      return uploadResult.id;
    } else {
      await lastValueFrom(
        this.storageBackend.update(
          invoice.storageId,
          this.fileNamingService.getFileName({
            fileType: FileType.Invoice,
            context: { invoice }
          }),
          blob
        )
      );
    }

    return invoice.storageId;
  }

  async toPdfMultiple(
    invoicesWithCreditNotes: Array<{
      invoice: Invoice;
      creditNote?: InvoiceLight;
    }>
  ): Promise<Blob> {
    const [
      invoiceTemplate,
      tailwindCss,
      invoiceHeaderTemplate,
      invoiceFooterTemplate,
      invoiceNote,
      defaultLetterhead
    ] = await Promise.all([
      fetch("/assets/ejs-templates/invoice/invoice.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/invoice/tailwind.css").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/header.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/invoice/invoice-footer.ejs").then(
        (response) => response.text()
      ),
      lastValueFrom(
        this.configurationBackend
          .loadBilling()
          .pipe(map((billing) => billing.invoice?.note))
      ),
      lastValueFrom(this.letterheadBackend.loadDefaultLight())
    ]);

    const { defaultLogoData, defaultSecondaryImageData } = await lastValueFrom(
      forkJoin({
        defaultLogoData: iif(
          () => !!defaultLetterhead?.header?.logo?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.logo!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        ),
        defaultSecondaryImageData: iif(
          () => !!defaultLetterhead?.header?.secondaryImage?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.secondaryImage!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        )
      })
    );

    const fiscalProfilesCache: Record<string, FiscalProfile> = {};

    const pages = await Promise.all(
      invoicesWithCreditNotes.map(async ({ invoice, creditNote }) => {
        const fiscalProfile: FiscalProfile | undefined =
          await (fiscalProfilesCache[invoice.fiscalProfile.id]
            ? Promise.resolve(fiscalProfilesCache[invoice.fiscalProfile.id])
            : lastValueFrom(
                defer(() => {
                  return this.fiscalProfileBackend
                    .load(invoice.fiscalProfile.id)
                    .pipe(
                      tap((fiscalProfile: FiscalProfile) => {
                        fiscalProfilesCache[fiscalProfile.id] = fiscalProfile;
                      }),
                      catchError(() => {
                        return of(undefined);
                      })
                    );
                })
              ));

        let fiscalProfileLetterhead: LetterheadLight | undefined;
        if (fiscalProfile?.letterheadId) {
          fiscalProfileLetterhead = await lastValueFrom(
            this.letterheadBackend.loadLight(fiscalProfile.letterheadId)
          );
        }

        let fiscalProfileLogoData: string | undefined;
        let fiscalProfileSecondaryImageData: string | undefined;

        if (fiscalProfileLetterhead) {
          const fiscalProfileLogosData = await lastValueFrom(
            forkJoin({
              fiscalProfileLogoData: iif(
                () => !!fiscalProfileLetterhead?.header?.logo?.id,
                defer(() =>
                  this.storageBackend
                    .download(fiscalProfileLetterhead?.header!.logo!.id!)
                    .pipe(
                      switchMap((downloadUrl: string) =>
                        from(
                          fetch(downloadUrl)
                            .then((response) => response.blob())
                            .then(
                              (blob) => new Blob([blob], { type: "image/png" })
                            )
                            .then((imageBlob) => blobToDataUrl(imageBlob))
                        )
                      ),
                      catchError(() => of(undefined))
                    )
                ),
                of(undefined)
              ),
              fiscalProfileSecondaryImageData: iif(
                () => !!fiscalProfileLetterhead?.header?.secondaryImage?.id,
                defer(() =>
                  this.storageBackend
                    .download(
                      fiscalProfileLetterhead?.header!.secondaryImage!.id!
                    )
                    .pipe(
                      switchMap((downloadUrl: string) =>
                        from(
                          fetch(downloadUrl)
                            .then((response) => response.blob())
                            .then(
                              (blob) => new Blob([blob], { type: "image/png" })
                            )
                            .then((imageBlob) => blobToDataUrl(imageBlob))
                        )
                      ),
                      catchError(() => of(undefined))
                    )
                ),
                of(undefined)
              )
            })
          );

          fiscalProfileLogoData = fiscalProfileLogosData.fiscalProfileLogoData;
          fiscalProfileSecondaryImageData =
            fiscalProfileLogosData.fiscalProfileSecondaryImageData;
        }

        const viewModel = invoiceToInvoicePrintViewModel({
          invoice,
          fiscalProfile,
          creditNote,
          moduleService: this.moduleService,
          regionService: this.regionService,
          fileNamingService: this.fileNamingService
        });

        const htmlHeadeRendered = this.templateService.render(
          invoiceHeaderTemplate,
          {
            ...viewModel,
            styles: "",
            content: defaultLetterhead?.header?.html || "",
            title: "",
            logo: fiscalProfileLogoData || defaultLogoData,
            secondaryImage:
              fiscalProfileSecondaryImageData || defaultSecondaryImageData,
            logoSize: defaultLetterhead?.header?.logo?.size || 1,
            secondaryImageSize:
              defaultLetterhead?.header?.secondaryImage?.size || 1,
            tailwindCss
          }
        );

        const htmlFooterRendered = this.templateService.render(
          invoiceFooterTemplate,
          {
            ...viewModel,
            content: defaultLetterhead?.footer?.html || "",
            tailwindCss
          }
        );

        const htmlRendered = this.templateService.render(invoiceTemplate, {
          ...viewModel,
          invoiceNote,
          tailwindCss
        });

        return {
          header: htmlHeadeRendered,
          content: htmlRendered,
          footer: htmlFooterRendered
        };
      })
    );

    return await this.printService.htmlToPdfMultiple({ pages });
  }

  async toPdf(invoice: Invoice, creditNote?: InvoiceLight): Promise<Blob> {
    const [
      invoiceTemplate,
      tailwindCss,
      invoiceHeaderTemplate,
      invoiceFooterTemplate
    ] = await Promise.all([
      fetch("/assets/ejs-templates/invoice/invoice.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/invoice/tailwind.css").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/header.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/invoice/invoice-footer.ejs").then(
        (response) => response.text()
      )
    ]);

    const [fiscalProfile, invoiceNote] = await Promise.all([
      lastValueFrom(this.fiscalProfileBackend.load(invoice.fiscalProfile.id)),
      lastValueFrom(
        this.configurationBackend
          .loadBilling()
          .pipe(map((billing) => billing.invoice?.note))
      )
    ]);

    let defaultLetterhead;
    if (fiscalProfile.letterheadId) {
      defaultLetterhead = await lastValueFrom(
        this.letterheadBackend.loadLight(fiscalProfile.letterheadId)
      );
    } else {
      defaultLetterhead = await lastValueFrom(
        this.letterheadBackend.loadDefaultLight()
      );
    }

    const viewModel = invoiceToInvoicePrintViewModel({
      invoice,
      fiscalProfile,
      creditNote,
      moduleService: this.moduleService,
      regionService: this.regionService,
      fileNamingService: this.fileNamingService
    });

    const { logoData, secondaryImageData } = await lastValueFrom(
      forkJoin({
        logoData: iif(
          () => !!defaultLetterhead?.header?.logo?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.logo!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        ),
        secondaryImageData: iif(
          () => !!defaultLetterhead?.header?.secondaryImage?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.secondaryImage!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        )
      })
    );

    const htmlHeadeRendered = this.templateService.render(
      invoiceHeaderTemplate,
      {
        ...viewModel,
        styles: "",
        content: defaultLetterhead?.header?.html || "",
        title: "",
        logo: logoData,
        secondaryImage: secondaryImageData,
        logoSize: defaultLetterhead?.header?.logo?.size || 1,
        secondaryImageSize:
          defaultLetterhead?.header?.secondaryImage?.size || 1,
        tailwindCss
      }
    );

    const htmlFooterRendered = this.templateService.render(
      invoiceFooterTemplate,
      {
        ...viewModel,
        content: defaultLetterhead?.footer?.html || "",
        tailwindCss
      }
    );

    const htmlRendered = this.templateService.render(invoiceTemplate, {
      ...viewModel,
      invoiceNote,
      tailwindCss
    });

    return await this.printService.htmlToPdf({
      header: htmlHeadeRendered,
      footer: htmlFooterRendered,
      content: htmlRendered
    });
  }

  async #toPaymentReceiptPdf(invoice: Invoice): Promise<Blob> {
    const [
      invoicePaymentReceiptTemplate,
      tailwindCss,
      invoiceHeaderTemplate,
      invoiceFooterTemplate
    ] = await Promise.all([
      fetch("/assets/ejs-templates/invoice/invoice-payment-receipt.ejs").then(
        (response) => response.text()
      ),
      fetch("/assets/ejs-templates/invoice/tailwind.css").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/header.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/invoice/invoice-footer.ejs").then(
        (response) => response.text()
      )
    ]);

    const [fiscalProfile, invoiceNote] = await Promise.all([
      lastValueFrom(this.fiscalProfileBackend.load(invoice.fiscalProfile.id)),
      lastValueFrom(
        this.configurationBackend
          .loadBilling()
          .pipe(map((billing) => billing.invoice?.note))
      )
    ]);

    let defaultLetterhead;
    if (fiscalProfile.letterheadId) {
      defaultLetterhead = await lastValueFrom(
        this.letterheadBackend.load(fiscalProfile.letterheadId)
      );
    } else {
      defaultLetterhead = await lastValueFrom(
        this.letterheadBackend.loadDefault()
      );
    }

    const viewModel = invoiceToInvoicePaymentReceiptViewModel({
      invoice,
      fiscalProfile,
      creditNote: undefined,
      moduleService: this.moduleService,
      regionService: this.regionService,
      fileNamingService: this.fileNamingService
    });

    const { logoData, secondaryImageData } = await lastValueFrom(
      forkJoin({
        logoData: iif(
          () => !!defaultLetterhead?.header?.logo?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.logo!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        ),
        secondaryImageData: iif(
          () => !!defaultLetterhead?.header?.secondaryImage?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.secondaryImage!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        )
      })
    );

    const htmlHeadeRendered = this.templateService.render(
      invoiceHeaderTemplate,
      {
        ...viewModel,
        styles: "",
        content: defaultLetterhead?.header?.html || "",
        title: "",
        logo: logoData,
        secondaryImage: secondaryImageData,
        logoSize: defaultLetterhead?.header?.logo?.size || 1,
        secondaryImageSize:
          defaultLetterhead?.header?.secondaryImage?.size || 1,
        tailwindCss
      }
    );

    const htmlFooterRendered = this.templateService.render(
      invoiceFooterTemplate,
      {
        ...viewModel,
        content: defaultLetterhead?.footer?.html || "",
        tailwindCss
      }
    );

    const htmlRendered = this.templateService.render(
      invoicePaymentReceiptTemplate,
      {
        ...viewModel,
        invoiceNote,
        tailwindCss
      }
    );

    return await this.printService.htmlToPdf({
      header: htmlHeadeRendered,
      footer: htmlFooterRendered,
      content: htmlRendered
    });
  }

  @UserNotify({
    successMessage: marker("Fattura bloccata"),
    errorMessage: marker("Impossibile bloccare la fattura")
  })
  lock(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.billing}/invoices/${id}/lock`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Fattura sbloccata"),
    errorMessage: marker("Impossibile sbloccare la fattura")
  })
  unlock(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.billing}/invoices/${id}/unlock`,
      {}
    );
  }

  loadNumberingGapLight(id: string): Observable<NumberingGapLight> {
    return this.http.get<NumberingGapLight>(
      `${environment.snoots.api.billing}/numbering-gaps/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  searchNumberingGapsLight(
    counterName: string,
    request: PageRequest
  ): Observable<PaginatedResult<NumberingGapLight>> {
    return this.http.get<PaginatedResult<NumberingGapLight>>(
      `${environment.snoots.api.billing}/numbering-gaps`,
      {
        params: omitUndefined({
          light: true,
          counter: counterName,
          p: request.page,
          s: request.pageSize
        })
      }
    );
  }

  validateSectional(id: string): Observable<LoadInvoiceSectionalResponseDto> {
    return this.http.get<LoadInvoiceSectionalResponseDto>(
      `${environment.snoots.api.billing}/invoices/${id}/sectional`
    );
  }

  updateSectional(
    invoiceId: string,
    body: {
      sectionalId: string;
      sequence?: string;
      name?: string;
      year?: boolean;
      prefix?: boolean;
    }
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.billing}/invoices/${invoiceId}/update-sectional`,
      body
    );
  }
}

function getImageDataFromOriginalHeader(header: string): string | undefined {
  const div = document.createElement("div");
  div.innerHTML = header;
  return div.querySelector("img")?.getAttribute("src") || undefined;
}

function extendDoc(invoice: Invoice): Invoice {
  if (!invoice.items) {
    invoice.items = [];
  }

  const sendingsTS = invoice.sendings
    ? invoice.sendings.filter((x) => x.type === "tessera-sanitaria")
    : [];

  const sendingStatusMapTS = sendingsTS.reduce<
    Record<string, InvoiceSendingStatus>
  >((acc, sending) => {
    acc[sending.id] = sending.status;
    return acc;
  }, {});

  const sendingErrorMessageMapTS = sendingsTS.reduce<Record<string, string>>(
    (acc, sending) => {
      acc[sending.id] =
        sending.messages
          ?.map((message) => `${message.code} - ${message.description}`)
          .join(", ") || "";
      return acc;
    },
    {}
  );

  let sendingStatusTS = last(sendingsTS)?.status;

  const sendingStatusTSAccepted: InvoiceSending | undefined = find(
    sendingsTS,
    (sending: InvoiceSending) => sending.status === "accepted"
  );
  if (sendingStatusTSAccepted) {
    sendingStatusTS = sendingStatusTSAccepted.status;
  }

  const sendingStatusTSWarning: InvoiceSending | undefined = find(
    sendingsTS,
    (sending: InvoiceSending) => sending.status === "warning"
  );
  if (sendingStatusTSWarning && !sendingStatusTSAccepted) {
    sendingStatusTS = sendingStatusTSWarning.status;
  }

  const sendingErrorMessageTS = last(sendingsTS)
    ?.messages?.map((message) => `${message.code} - ${message.description}`)
    .join(", ");

  const sendingsFE =
    invoice.sendings?.filter(
      (x) =>
        x.type === "fatturazione-elettronica" || x.type === "electronic-invoice"
    ) || [];

  const sendingStatusMapFE = sendingsFE.reduce<
    Record<string, InvoiceSendingStatus>
  >((acc, sending) => {
    acc[sending.id] = sending.status;
    return acc;
  }, {});

  const sendingErrorMessageMapFE = sendingsFE.reduce<Record<string, string>>(
    (acc, sending) => {
      acc[sending.id] =
        sending.messages
          ?.map((message) => `${message.code} - ${message.description}`)
          .join(", ") || "";
      return acc;
    },
    {}
  );

  let sendingStatusFE = last(sendingsFE)?.status;

  const sendingStatusFEAccepted: InvoiceSending | undefined = find(
    sendingsFE,
    (sending: InvoiceSending) => sending.status === "accepted"
  );
  if (sendingStatusFEAccepted) {
    sendingStatusFE = sendingStatusFEAccepted.status;
  }

  const sendingStatusFEWarning: InvoiceSending | undefined = find(
    sendingsFE,
    (sending: InvoiceSending) => sending.status === "warning"
  );
  if (sendingStatusFEWarning && !sendingStatusFEAccepted) {
    sendingStatusFE = sendingStatusFEWarning.status;
  }

  const sendingErrorMessageFE = last(invoice.sendings)
    ?.messages?.map((message) => `${message.code} - ${message.description}`)
    .join(", ");

  const isDraft: boolean = !invoice.emittedBy;

  return {
    ...invoice,
    humanId: isDraft
      ? generateReadableNotulaIdSync(invoice.id, invoice.customer?.id || "")
      : undefined,
    expired: invoiceIsExpired(invoice),
    expiredAt: invoiceGetExpiredAt(invoice),
    paymentMethodId: invoice.method?.id,
    deleted: !!invoice.deletedAt,
    paymentInstallments:
      !!invoice.paymentInstallments && isObject(invoice.paymentInstallments)
        ? values(invoice.paymentInstallments)
        : invoice.paymentInstallments,
    isDraft,
    status: statusInvoice(invoice),
    discountAmount: invoice.taxedTotal - invoice.discountedTotal,
    notTaxableTotal:
      !invoice.vatSummaryLines || invoice.vatSummaryLines.length === 0
        ? 0
        : sum(
            invoice.vatSummaryLines
              .filter((x) => x.tax === 0)
              .map((x) => x.detail.rateable)
          ),
    drugsAssignmentRateable: sum(
      invoice.items
        .filter((x) => x.type === "drug-assignment")
        .map((x) => x.rateable)
    ),
    parapharmaceuticalRateable: sum(
      invoice.items
        .filter((x) => x.type === "parapharmaceutical")
        .map((x) => x.rateable)
    ),
    medicalServicesRateable: sum(
      invoice.items
        .filter((x) => x.type === "medical-service")
        .map((x) => x.rateable)
    ),
    productsSaleRateable: sum(
      invoice.items
        .filter((x) => x.type === "product-sale")
        .map((x) => x.rateable)
    ),
    sendingsTS,
    sendingStatusTS,
    sendingStatusMapTS,
    sendingErrorMessageMapTS,
    sendingErrorMessageTS,
    sendingsFE,
    sendingStatusFE,
    sendingStatusMapFE,
    sendingErrorMessageMapFE,
    sendingErrorMessageFE,
    lockedWarning:
      invoice.type === "sales-invoice" && sendingStatusTS === "warning",

    xmlExportable: !!invoice.emittedAt,
    stampTaxAllowed:
      invoice.fiscalProfile?.taxRegime ===
      InvoiceFiscalProfileTaxRegime.FLAT_TAX_REGIME
        ? invoice.adjustedTotal > 77.47
        : sum(
            invoice.vatSummaryLines
              ?.filter((x) => x.tax === 0)
              .map((x) => x.detail.rateable) || []
          ) > 77.47 || false,
    additionalGeneralData: invoice.additionalGeneralData
      ? first(<any[]>invoice.additionalGeneralData)
      : invoice.additionalGeneralData,

    someSendingStatusFEIsAccepted: invoice?.sendingsFE?.some(
      (send) => send.status === "accepted"
    ),
    someSendingStatusFEIsWarning: invoice?.sendingsFE?.some(
      (send) => send.status === "warning"
    ),
    someSendingStatusTSIsAccepted: invoice?.sendingsTS?.some(
      (send) => send.status === "accepted"
    ),
    someSendingStatusTSIsWarning: invoice?.sendingsTS?.some(
      (send) => send.status === "warning"
    ),
    advanceTotalAmount: Math.abs(
      sum(
        invoice?.items
          .filter((i) => i.type === "advance-payment")
          .map((i) => i.rateable)
      ) || 0
    )
  };
}

function extendDocLight(invoiceLight: InvoiceLight): InvoiceLight {
  invoiceLight.expired = invoiceIsExpired(invoiceLight);
  invoiceLight.expiredAt = invoiceGetExpiredAt(invoiceLight);

  const idDraft = !invoiceLight.emittedBy;
  if (idDraft) {
    invoiceLight.humanId = generateReadableNotulaIdSync(
      invoiceLight.id,
      invoiceLight.customer?.id || ""
    );
  }

  return invoiceLight;
}

function invoiceToInvoicePaymentReceiptViewModel({
  invoice,
  fiscalProfile,
  creditNote,
  regionService,
  moduleService,
  fileNamingService
}: {
  invoice: Invoice;
  fiscalProfile: FiscalProfile;
  creditNote?: InvoiceLight;
  regionService: RegionService;
  moduleService: ModuleService;
  fileNamingService: FileNamingService;
}): InvoicePaymentReceiptPrintViewModel {
  const viewModel = invoiceToInvoicePrintViewModel({
    invoice,
    fiscalProfile,
    creditNote,
    regionService,
    moduleService,
    fileNamingService
  });

  return <InvoicePaymentReceiptPrintViewModel>{
    ...viewModel,

    amountCashedInLabel: sntTranslate(marker("Importo incassato")),
    dateLabel: sntTranslate(marker("Data")),
    paymentMethodLabel: sntTranslate(marker("Metodo di pagamento")),
    paymentReceiptLabel: sntTranslate(marker("Ricevuta di pagamento")),

    paymentReceipts:
      invoice.payments?.map((payment: InvoicePaymentRecepit) => {
        return {
          amountCashedIn: formatCurrency({
            value: payment.amount,
            regionService
          }),
          description: payment.paymentMethod?.name
            ? sntTranslate(marker(payment.paymentMethod.name))
            : "",
          date: payment.paidAt ? dateUtil(payment.paidAt).format("L") : ""
        };
      }) || []
  };
}

function invoiceToInvoicePrintViewModel({
  invoice,
  fiscalProfile,
  creditNote,
  regionService,
  moduleService,
  fileNamingService
}: {
  invoice: Invoice;
  fiscalProfile: FiscalProfile | undefined;
  creditNote?: InvoiceLight;
  regionService: RegionService;
  moduleService: ModuleService;
  fileNamingService: FileNamingService;
}): InvoicePrintViewModel {
  const percentFormatter = new Intl.NumberFormat(regionService.get(), {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  });

  const address = invoice.customer?.address || {
    address: "",
    zipCode: "",
    city: "",
    state: "",
    country: ""
  };

  const vatNatureList: VatNatureType[] = flatten(
    invoice.vatSummaryLines.map((x) => x.detail.vatNatureList || [])
  );

  const vatNatureNotice = {
    vatNoticeN21: some(vatNatureList, (x) => x === VatNatureType.N2_1)
      ? sntTranslate(
          marker(
            "Operazioni non soggette ad IVA ai sensi degli artt. da 7 a 7-septies del DPR 633/72"
          )
        )
      : "",
    vatNoticeN22: some(vatNatureList, (x) => x === VatNatureType.N2_2)
      ? sntTranslate(marker("Operazioni non soggette ad IVA - altri casi"))
      : "",
    vatNoticeN31: some(vatNatureList, (x) => x === VatNatureType.N3_1)
      ? sntTranslate(
          marker(
            "Operazioni non imponibili ai sensi dell'art. 8, comma 1, lettera a) del DPR 633/72"
          )
        )
      : "",
    vatNoticeN32: some(vatNatureList, (x) => x === VatNatureType.N3_2)
      ? sntTranslate(
          marker(
            "Operazioni non imponibili ai sensi dell'art. 41, DL n. 331/93"
          )
        )
      : "",
    vatNoticeN33: some(vatNatureList, (x) => x === VatNatureType.N3_3)
      ? sntTranslate(
          marker("Operazioni non imponibili - cessioni verso San Marino")
        )
      : ""
  };

  const isTaxInvoiceLabelEnabled = moduleService.canBeActivatedSync(
    MODULES.PRINT_LABEL_TAX_INVOICE
  );

  const isCustomerAddressEnabled = moduleService.canBeActivatedSync(
    MODULES.INVOICE_CUSTOMER_ADDRESS
  );

  const isItalianAdditionFieldsEnabled = regionService.get() === Regions.IT;

  return {
    documentType: invoice.isDraft
      ? sntTranslate(marker("Bozza"))
      : sntTranslate(invoice.type),
    draft: invoice.isDraft || false,
    invoiceNumber: invoice.number,
    invoiceDate: dateUtil(invoice.emittedAt || invoice.createdAt)
      .locale(regionService.getLocale())
      .format("L"),

    businessName: invoice.fiscalProfile.businessName || "",
    businessAddress: formatAddress(invoice.fiscalProfile.address),
    businessVatCode: `P.I. ${invoice.fiscalProfile.vatNumber}`,
    businessFiscalCode: `C.F. ${
      invoice.fiscalProfile.ssn || invoice.fiscalProfile.vatNumber
    }`,

    customerAddressLine1: `${address.address || ""}`,
    customerAddressLine2: `${address.zipCode || ""} - ${address.city || ""} (${
      address.state || ""
    })`,

    customerFiscalCode: invoice.customer?.ssn || "",
    customerNominative: getCustomerNominative(invoice.customer),
    customerVatCode: invoice.customer?.vatNumber || "",

    discountedTotal: formatCurrency({
      value: round(invoice.discountedTotal, 2),
      regionService
    }),
    adjustedTotal: formatCurrency({
      value: round(invoice.adjustedTotal, 2),
      regionService
    }),

    withholdingTax: percentFormatter.format(
      invoice.fiscalProfile.withholdingTax
    ),

    withholdingTaxAmount: formatCurrency({
      value: round(-1 * invoice.withholdingTaxAmount, 2),
      regionService
    }),
    hideWithholdingTaxAmount: invoice.withholdingTaxAmount === 0,

    vatSplit: !!invoice.vatSplit,

    humanId: invoice.humanId || "",

    rows: toPairs(
      groupBy<{
        key: string;
        item: InvoicePrintViewModelRow;
      }>(
        invoice.items
          .map((item) => {
            return {
              rateTableItemId: item.rateTableItemId,
              name: item.name,
              description: item.description,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              rateable: item.rateable,
              netTotal: item.adjustedTotal,
              vat: item.vat,
              patientName: item.patientName || "",
              patientMicrochip: item.patientMicrochip || "",
              discount: item.discount,
              enpav: item.enpavAmount || 0,
              type: item.type
            };
          })
          .map((item) => ({
            key: `${item.rateTableItemId}-${item.name}-${item.description}-${item.unitPrice}-${item.patientName}-${item.discount}-${item.patientMicrochip}-${item.vat}`,
            item
          })),
        (x) => x.key
      )
    )
      .map(([, values]) => {
        const items = values.map((x) => x.item);
        const item: InvoicePrintViewModelRow = items[0];

        return {
          ...item,
          quantity: sum(items.map((x) => x.quantity)),
          rateable: sum(items.map((x) => x.rateable)),
          netTotal: sum(items.map((x) => x.netTotal))
        };
      })
      .map((item: InvoicePrintViewModelRow) => {
        return {
          ...item,
          quantity: `${item.quantity}`,
          unitPrice: formatCurrency({
            value: round(item.unitPrice, 2),
            regionService
          }),
          rateable: formatCurrency({
            value: round(item.rateable, 2),
            regionService
          }),
          netTotal: formatCurrency({
            value: round(item.netTotal, 2),
            regionService
          }),
          vat: percentFormatter.format(item.vat),
          enpav: formatCurrency({ value: round(item.enpav, 2), regionService }),
          discount: item.discount ? percentFormatter.format(item.discount) : ""
        };
      }),

    note: invoice.note || "",
    deadLines:
      invoice.paymentInstallments?.map((item) => {
        return `${dateUtil(item.expiresAt).format("DD/MM/YYYY")}: ${formatCurrency(
          { value: round(item.amount, 2), regionService }
        )}`;
      }) || [],
    paymentInfoLines: [
      sntTranslate(marker("Pagamento indirizzato a:")),
      sntTranslate(marker("Intestato a:")),
      sntTranslate(marker("Indirizzo:")),
      sntTranslate(marker("IBAN"))
    ],

    drugsAssignmentRateable:
      invoice.drugsAssignmentRateable > 0
        ? formatCurrency({
            value: round(invoice.drugsAssignmentRateable, 2),
            regionService
          })
        : "",

    parapharmaceuticalRateable:
      invoice.parapharmaceuticalRateable > 0
        ? formatCurrency({
            value: round(invoice.parapharmaceuticalRateable, 2),
            regionService
          })
        : "",

    medicalServicesRateable:
      invoice.medicalServicesRateable > 0
        ? formatCurrency({
            value: round(invoice.medicalServicesRateable, 2),
            regionService
          })
        : "",

    productsSaleRateable:
      invoice.productsSaleRateable > 0
        ? formatCurrency({
            value: round(invoice.productsSaleRateable, 2),
            regionService
          })
        : "",

    flatRateProfile: invoice.fiscalProfile
      ? invoice.fiscalProfile.taxRegime === "flat-tax-regime"
      : false,

    vatSummaryLines: invoice.vatSummaryLines.map((x) => ({
      tax: percentFormatter.format(x.tax),
      total: formatCurrency({
        value: round(x.detail.taxAmount, 2),
        regionService
      }),
      hide: x.detail.taxAmount === 0
    })),

    adjustment: invoice.adjustment
      ? formatCurrency({
          value: round(invoice.adjustment || 0, 2),
          regionService
        })
      : "",
    hideAdjustement: !invoice.adjustment || invoice.adjustment === 0,

    enpav: invoice.fiscalProfile
      ? percentFormatter.format(invoice.fiscalProfile.enpav?.rate || 0)
      : "",

    enpavAmount: formatCurrency({ value: invoice.enpavAmount, regionService }),
    hideEnpavAmount: invoice.enpavAmount === 0,

    footerName: fileNamingService.getFileName({
      fileType: FileType.Invoice,
      context: { invoice },
      omitDate: true,
      omitFileExtension: true
    }),

    noVatRateableTotal: invoice.noVatRateableTotal
      ? formatCurrency({
          value: round(invoice.noVatRateableTotal, 2),
          regionService
        })
      : "",
    notTaxableTotal: invoice.notTaxableTotal
      ? formatCurrency({
          value: round(invoice.notTaxableTotal || 0, 2),
          regionService
        })
      : "",
    hideNotTaxableTotal:
      (invoice.notTaxableTotal || invoice.notTaxableTotal || 0) === 0,

    vatRateableTotal: invoice.vatRateableTotal
      ? formatCurrency({
          value: round(invoice.vatRateableTotal, 2),
          regionService
        })
      : "",
    advanceTotalAmount:
      invoice.advanceTotalAmount && invoice.advanceTotalAmount > 0
        ? formatCurrency({
            value: round(invoice.advanceTotalAmount, 2),
            regionService
          })
        : "",
    rateableTotal:
      (invoice.rateableTotal ||
        invoice.items.some((i) => i.type !== "advance-payment")) &&
      invoice.type !== "advance-invoice" &&
      invoice.type !== "credit-note"
        ? formatCurrency({
            value: round(
              sum([invoice.rateableTotal, invoice.advanceTotalAmount]),
              2
            ),
            regionService
          })
        : "",
    hideRateableTotal:
      (invoice.vatRateableTotal || 0) === 0 &&
      [
        InvoiceFiscalProfileTaxRegime.NORMAL_REGIME,
        InvoiceFiscalProfileTaxRegime.FLAT_TAX_REGIME
      ].includes(invoice.fiscalProfile.taxRegime),

    taxedTotal: formatCurrency({
      value: round(invoice.taxedTotal, 2),
      regionService
    }),

    discount: percentFormatter.format(invoice.discount || 0),
    hideDiscount: invoice.discount === 0,

    discountAmount: formatCurrency({
      value: invoice.discountAmount || 0,
      regionService
    }),

    paidTotal: formatCurrency({
      value: round(invoice.paidTotal, 2),
      regionService
    }),
    hidePaidTotal: invoice.paidTotal === 0,

    toBePaid: formatCurrency({
      value: round(invoice.toBePaid || 0, 2),
      regionService
    }),
    hideToBePaid: invoice.toBePaid === 0,

    paid: invoice.paid,

    paymentMethod: invoice.method ? invoice.method.name : "",
    paymentMethodValue: invoice.method?.type,

    paymentTerm: invoice.paymentTerm ? sntTranslate(invoice.paymentTerm) : "",

    payments: invoice.payments.map((x) => ({
      amount: formatCurrency({ value: round(x.amount, 2), regionService }),
      method: x.paymentMethod?.name || "",
      date: dateUtil(x.paidAt).format("DD/MM/YYYY")
    })),

    iban: invoice.method?.bankAccount?.iban
      ? invoice.method.bankAccount.iban
      : fiscalProfile.iban
        ? ibantools.friendlyFormatIBAN(fiscalProfile.iban)
        : "",

    hideStampTax: !invoice.stampTax,
    stampTax: invoice.stampTax
      ? formatCurrency({
          value: round(invoice.stampTaxAmount || 0, 2),
          regionService
        })
      : "",

    hideVat: false,

    hideAdditionalData: invoice.customer?.type !== "public",
    additionalDataType: invoice.additionalGeneralData?.type
      ? sntTranslate(
          `invoice-additionaldata-type-${invoice.additionalGeneralData.type}`
        )
      : "",
    additionalDataDocumentDate: invoice.additionalGeneralData?.documentDate
      ? dateUtil(invoice.additionalGeneralData?.documentDate).format(
          "DD/MM/YYYY"
        )
      : "",
    additionalDataDocumentNumber:
      invoice.additionalGeneralData?.documentNumber || "",
    additionalDataContractOrAgreementCode:
      invoice.additionalGeneralData?.contractOrAgreementCode || "",
    additionalDataCig: invoice.additionalGeneralData?.cig || "",
    additionalDataCup: invoice.additionalGeneralData?.cup || "",

    hasVatNotice: some(values(vatNatureNotice), (x) => !!x),
    vatNoticeN21: vatNatureNotice.vatNoticeN21,
    vatNoticeN22: vatNatureNotice.vatNoticeN22,
    vatNoticeN31: vatNatureNotice.vatNoticeN31,
    vatNoticeN32: vatNatureNotice.vatNoticeN32,
    vatNoticeN33: vatNatureNotice.vatNoticeN33,
    reversedInvoiceNumber:
      invoice.reversedInvoice?.number || creditNote?.number || "",
    reversedInvoiceAt:
      dateUtil(invoice.reversedInvoice?.emittedAt).format("DD/MM/YYYY") ||
      dateUtil(invoice.reversedAt).format("DD/MM/YYYY") ||
      "",

    taxesAmount: formatCurrency({
      value: round(invoice.taxesAmount, 2),
      regionService
    }),

    notulaLabel: sntTranslate(marker("Notule")),
    dataLabel: sntTranslate(marker("Data")),
    tipoLabel: sntTranslate(marker("Tipo")),
    numeroDocumentoLabel: sntTranslate(marker("Numero documento")),
    dataDocumentoLabel: sntTranslate(marker("Data documento")),
    commessaConvenzioneLabel: sntTranslate(marker("Commessa/convenzione")),
    spettabileLabel: sntTranslate(marker("Spettabile")),
    pivaLabel: sntTranslate(marker("P.IVA")),
    cfLabel: sntTranslate(marker("C.F.")),
    descrizioneLabel: sntTranslate(marker("Descrizione")),
    qtaLabel: sntTranslate(marker("Qta")),
    discountLabel: sntTranslate(marker("Sconto")),
    pUnitarioLabel: sntTranslate(marker("P. unitario")),
    totaleLabel: sntTranslate(marker("Totale")),
    ivaLabel: sntTranslate(marker("IVA")),
    imponibileLabel: `${sntTranslate(marker("Imponibile"))} ${sntTranslate(marker("IVA"))}`,
    pazienteLabel: sntTranslate(marker("Paziente")),
    prestazioniLabel: sntTranslate(marker("Prestazioni")),
    prodottiLabel: sntTranslate(marker("Prodotti")),
    farmaciLabel: sntTranslate(marker("Farmaci")),
    parafarmaciLabel: sntTranslate(marker("Parafarmaci")),
    accontoLabel: sntTranslate(marker("Acconto")),
    nonImponibileLabel: sntTranslate(marker("Non imponibile")),
    totaleFatturaLabel: sntTranslate(marker("Totale fattura")),
    arrotondamentoLabel: sntTranslate(marker("Arrotondamento")),
    totaleDaPagareLabel: sntTranslate(marker("Totale da pagare")),
    incassatoLabel: sntTranslate(marker("Incassato")),
    residuoLabel: sntTranslate(marker("Residuo")),
    incassiLabel: sntTranslate(marker("Incassi")),
    terminiDiPagamentoLabel: sntTranslate(marker("Termini di pagamento")),
    noteLabel: sntTranslate(marker("Note")),
    questaNotaDiCreditoRettificaLaFatturaLabel: sntTranslate(
      marker("Questa nota di credito rettifica la fattura")
    ),
    numeroLabel: sntTranslate(marker("Numero")),
    emessaIlLabel: sntTranslate(marker("Emessa il")),
    pagamentoIndirizzatoALabel: sntTranslate(marker("Pagamento indirizzato a")),

    enableTaxInvoiceLabel: isTaxInvoiceLabelEnabled,
    enableCustomerAddress: isCustomerAddressEnabled,

    disableCodeCig: !isItalianAdditionFieldsEnabled,
    disableCodeCup: !isItalianAdditionFieldsEnabled,
    disableEnpavAmount: !isItalianAdditionFieldsEnabled,
    disableFlatRateProfileNotice: !isItalianAdditionFieldsEnabled,
    disableImpostaDiBollo: !isItalianAdditionFieldsEnabled,
    disableRitenutaDAcconto: !isItalianAdditionFieldsEnabled,
    disableVatSplitNotice: !isItalianAdditionFieldsEnabled,

    isInvoiceCompleteDetails: moduleService.canBeActivatedSync(
      MODULES.INVOICE_COMPLETE_DETAILS
    )
  };
}

export function statusInvoice(invoice: Invoice): string {
  const firstDate = last(
    [
      invoice.reversedAt ? dateUtil(invoice.reversedAt).unix() : 0,
      invoice.deliveredAt ? dateUtil(invoice.deliveredAt).unix() : 0,
      invoice.emittedAt ? dateUtil(invoice.emittedAt).unix() : 0
    ].sort()
  );

  let status = "draft";

  if (firstDate === dateUtil(invoice.reversedAt).unix() && invoice.reversedAt) {
    status = "reversed";
  }
  if (
    firstDate === dateUtil(invoice.deliveredAt).unix() &&
    invoice.deliveredAt
  ) {
    status = "delivered";
  }
  if (firstDate === dateUtil(invoice.emittedAt).unix() && invoice.emittedAt) {
    status = "emitted";
  }
  return status;
}

/**
 * Pulisce e tronca il nominativo in modo sincrono
 */
function formatName(name: string, limit: number = 5): string {
  return (
    name
      .trim()
      .toUpperCase()
      // Rimuove accenti e caratteri non-ASCII in modo semplice e leggero
      .replace(/[ÀÁÂÃÄÅ]/g, "A")
      .replace(/[ÈÉÊË]/g, "E")
      .replace(/[ÌÍÎÏ]/g, "I")
      .replace(/[ÒÓÔÕÖ]/g, "O")
      .replace(/[ÙÚÛÜ]/g, "U")
      .replace(/[^A-Z]/g, "")
      .substring(0, limit)
  );
}

/**
 * Hash FNV-1a: ultra-veloce, sincrono e leggero.
 * Trasforma una stringa in un intero a 32 bit.
 */
function fnv1a(str: string): number {
  let hash = 0x811c9dc5; // Offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    // Moltiplicazione per il primo FNV a 32 bit (0x01000193)
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0; // Forza il risultato a intero positivo a 32 bit
}

/**
 * Genera il codice Notula in modo sincrono
 */
function generateReadableNotulaIdSync(
  notulaId: string,
  clientId: string
): string {
  // Hash sincrono della combinazione degli ID
  const numericValue = fnv1a(`${notulaId}-${clientId}`);

  // Generiamo il PIN a 6 cifre
  const pinNumber = numericValue % 1000000;
  const pinString = pinNumber.toString().padStart(6, "0");

  // Formato: 123-456
  return `${pinString.slice(0, 3)}-${pinString.slice(3)}`;
}
